import { lazy, Suspense } from 'react';
import { Outlet, Navigate, useRoutes } from 'react-router-dom';
import { AppLayout, NoNavLayout } from 'src/layouts/dashboard';

import { MESSAGES_ROUTE } from './paths';
import RequireAuth from './components/RequireAuth';

/**
 * Pages don't declare an `id` prop themselves; RequireAuth reads it off the
 * routed element (`element.props.id`) to check access, so the lazy wrappers
 * are typed to accept it.
 */
type Page = React.LazyExoticComponent<React.ComponentType<{ id?: string }>>;

export const LoginPage: Page = lazy(() => import('../pages/login'));
export const Page404: Page = lazy(() => import('../pages/page-not-found'));
export const InstructionsPage: Page = lazy(() => import('../pages/instructions'));
export const AppPage: Page = lazy(() => import('../pages/app'));
export const GuestPage: Page = lazy(() => import('../pages/guest'));
// The messages pages already declare their own required `id` prop.
export const MessagesListPage = lazy(() => import('../pages/messages-list'));
export const MessageThreadPage = lazy(() => import('../pages/message-thread'));

// ----------------------------------------------------------------------

export default function Router() {
  const routes = useRoutes([
    {
      element: (
        <NoNavLayout>
          <Suspense>
            <Outlet />
          </Suspense>
        </NoNavLayout>
      ),
      children: [
        { path: ':lang?/login/', element: <LoginPage /> },
        { path: 'instructions/:type?', element: <InstructionsPage /> },
      ],
    },
    {
      element: (
        <AppLayout>
          <Suspense>
            <Outlet />
          </Suspense>
        </AppLayout>
      ),
      children: [
        { index: true, element: <RequireAuth element={null} /> },
        { path: MESSAGES_ROUTE, element: <RequireAuth element={<MessagesListPage id="app" />} /> },
        { path: `${MESSAGES_ROUTE}/:threadId`, element: <RequireAuth element={<MessageThreadPage id="app" />} /> },
        { path: 'app/:postback?', element: <RequireAuth element={<AppPage id="app" />} /> },
        { path: 'guest', element: <GuestPage /> },
      ],
    },
    {
      path: '404',
      element: <Page404 />,
    },
    {
      path: '*',
      element: <Navigate to="/404" replace />,
    },
  ]);

  return routes;
}
